import { Ticket } from '@composites/Tickets';
import { ROOT_URL } from './apiHooks';

export interface WebsiteContentTicket {
  ticket: string;
  page: string; // 'icebreaker', 'evening-networking', 'home', 'mkt-vip-pass'
}

export const fetchTicketsByPageType = async (
  eventSlug: string,
  pageType:
    | 'icebreaker'
    | 'evening-networking'
    | 'home'
    | 'mkt-vip-pass'
    | 'team-ticket'
    | 'oem-ticket'
    | 'bp-vip-client-ticket'
    | 'networking-dinner'
): Promise<Ticket[]> => {
  try {
    // First, fetch the event details to get website content
    const eventResponse = await fetch(`${ROOT_URL}/events?slug=${eventSlug}`);
    const eventData = await eventResponse.json();
    const event = eventData.result?.data?.[0];

    if (!event?.website_content?.tickets) {
      console.warn('No website content tickets found for event:', eventSlug);
      return [];
    }

    // Filter tickets based on page type
    let relevantTicketIds: string[] = [];

    if (pageType === 'icebreaker') {
      // For icebreaker page, take the first ticket with page 'icebreaker'
      const icebreakerTickets = event.website_content.tickets.filter(
        (ticket: WebsiteContentTicket) => ticket.page === 'icebreaker'
      );
      if (icebreakerTickets.length > 0) {
        relevantTicketIds = [icebreakerTickets[0].ticket];
      }
    } else if (pageType === 'evening-networking') {
      // For evening-networking page, take the first ticket with page 'evening-networking'
      const eveningNetworkingTickets = event.website_content.tickets.filter(
        (ticket: WebsiteContentTicket) => ticket.page === 'evening-networking'
      );
      if (eveningNetworkingTickets.length > 0) {
        relevantTicketIds = [eveningNetworkingTickets[0].ticket];
      }
    } else if (pageType === 'home') {
      // For home page, take all tickets with page 'home'
      relevantTicketIds = event.website_content.tickets
        .filter((ticket: WebsiteContentTicket) => ticket.page === 'home')
        .map((ticket: WebsiteContentTicket) => ticket.ticket);
    } else if (pageType === 'mkt-vip-pass') {
      // For mkt-vip-pass page, take the first ticket with page 'mkt-vip-pass'
      const mktVipPassTickets = event.website_content.tickets.filter(
        (ticket: WebsiteContentTicket) => ticket.page === 'mkt-vip-pass'
      );
      if (mktVipPassTickets.length > 0) {
        relevantTicketIds = [mktVipPassTickets[0].ticket];
      }
    } else if (pageType === 'team-ticket') {
      // For mkt-vip-pass page, take the first ticket with page 'team-ticket'
      const teamTicket = event.website_content.tickets.filter(
        (ticket: WebsiteContentTicket) => ticket.page === 'team-ticket'
      );
      if (teamTicket.length > 0) {
        relevantTicketIds = [teamTicket[0].ticket];
      }
    } else if (pageType === 'bp-vip-client-ticket') {
      const BpVipClientTicket = event.website_content.tickets.filter(
        (ticket: WebsiteContentTicket) => ticket.page === 'bp-vip-client-ticket'
      );
      if (BpVipClientTicket.length > 0) {
        relevantTicketIds = [BpVipClientTicket[0].ticket];
      }
    } else if (pageType === 'networking-dinner') {
      const networkingDinnerTicket = event.website_content.tickets.filter(
        (ticket: WebsiteContentTicket) => ticket.page === 'networking-dinner'
      );
      if (networkingDinnerTicket.length > 0) {
        relevantTicketIds = [networkingDinnerTicket[0].ticket];
      }
    } else if (pageType === 'oem-ticket') {
      // For oem-ticket page, take the first ticket with page 'oem-ticket'
      const teamTicket = event.website_content.tickets.filter(
        (ticket: WebsiteContentTicket) => ticket.page === 'oem-ticket'
      );
      if (teamTicket.length > 0) {
        relevantTicketIds = [teamTicket[0].ticket];
      }
    }

    if (relevantTicketIds.length === 0) {
      return [];
    }

    // Now fetch the actual ticket details using the ticket IDs
    const ticketsResponse = await fetch(
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      `${ROOT_URL}/tickets?event=${event?.id ?? ''}`
    );
    const ticketsData = await ticketsResponse.json();
    const allTickets = ticketsData.result?.data || [];

    // Filter tickets by the relevant IDs and other criteria
    const relevantTickets = allTickets
      .filter(
        (ticket: Ticket) =>
          relevantTicketIds.includes(ticket.id) &&
          ticket.visibility === 'public'
      )
      .sort((a: Ticket, b: Ticket) => a.priority - b.priority);

    return relevantTickets;
  } catch (error) {
    console.error('Error fetching tickets by page type:', error);
    return [];
  }
};

export const getPageTypeFromPathname = (
  pathname: string
):
  | 'icebreaker'
  | 'evening-networking'
  | 'home'
  | 'mkt-vip-pass'
  | 'team-ticket'
  | 'oem-ticket'
  | 'bp-vip-client-ticket'
  | 'networking-dinner' => {
  const segments = pathname.split('/').filter(Boolean);
  const lastSegment = segments[segments.length - 1];
  console.log('lastSegment = ', lastSegment);

  if (lastSegment === 'icebreaker') {
    return 'icebreaker';
  } else if (lastSegment === 'evening-networking') {
    return 'evening-networking';
  } else if (lastSegment === 'mkt-vip-pass') {
    return 'mkt-vip-pass';
  } else if (lastSegment === 'team-ticket') {
    return 'team-ticket';
  } else if (lastSegment === 'oem-ticket') {
    return 'oem-ticket';
  } else if (lastSegment === 'bp-vip-client-ticket') {
    return 'bp-vip-client-ticket';
  } else if (lastSegment === 'networking-dinner') {
    return 'networking-dinner';
  } else {
    return 'home';
  }
};
